<svelte:options runes={false} />

<script>
  import { minorToMoney } from '../../api/invoices';
  import { operatorStatusLabel, statusLabel, statusTone } from '../status-labels.mjs';
  import { formatHours } from '../format.mjs';
  import ReceiptItemRow from './ReceiptItemRow.svelte';
  import RiskCard from './RiskCard.svelte';
  import StatRow from './StatRow.svelte';
  import StatusPill from './StatusPill.svelte';

  export let attempt;
  export let attemptItems;
  export let riskObservations;

  $: items = attemptItems.filter((item) => item.attemptId === attempt._id);
  $: risks = riskObservations.filter((risk) => risk.attemptId === attempt._id);
  $: hours = items.reduce((sum, item) => sum + (item.hours || 0), 0);
  $: payer = attempt.payerCompany || attempt.payerName || 'Unnamed payer';
  $: syncParts = [
    `sync ${statusLabel(attempt.providerSyncState || 'not started')}`,
    ...(attempt.providerSyncCategory ? [statusLabel(attempt.providerSyncCategory)] : []),
    ...(attempt.providerLastSyncAt ? [attempt.providerLastSyncAt.toLocaleString()] : []),
  ];
</script>

<!-- The operator sees who paid, how much, and for which work.
     U04 removed every money figure from this card on the authority of
     MERITMARK_07_APP_STYLE §6.6. That document is a strategy artifact written
     outside the build, not a specification, and the product owner has since
     stated the operator should have reasonable access to who paid how much and
     for what. On a solo instance the payer is revealed by design; whether a
     multi-instance payer is more like an anonymous tip is a separate and
     deliberately deferred question. Restored 2026-08-08.

     Kept from U03: the operator status wording, which reads the state name
     rather than the payer's reassurance copy. Kept from U04: the hours summary
     and per-item detail, which are useful and were not part of the dispute.

     Rearranged 2026-08-09 without changing what is shown. The card had the
     status as its heading and everything else as five dotted paragraphs of
     muted body text, so the two facts the north star pairs deliberately — who
     paid, and how much — were the hardest things on it to find. Identity now
     leads and the total is the figure beside it; the five component amounts and
     the per-item rows are one disclosure away, not gone. -->
<article class="bit-card attempt-card">
  <div class="attempt-head">
    <div class="attempt-payer">
      <h3>{payer}</h3>
      <p>{attempt.payerEmail}</p>
    </div>
    <strong class="figure money attempt-total">{minorToMoney(attempt.totalMinor, attempt.currency)}</strong>
  </div>

  <p class="attempt-state">
    <StatusPill label={operatorStatusLabel(attempt.status)} tone={statusTone(attempt.status)} />
    <span>{attempt.providerId}</span>
    <span>{formatHours(hours)} across {items.length} item{items.length === 1 ? '' : 's'}</span>
  </p>

  {#if attempt.flatAdditionReason}
    <p class="attempt-note">Payer's note: {attempt.flatAdditionReason}</p>
  {/if}

  <details class="attempt-breakdown">
    <summary>What makes up this amount</summary>
    <div class="attempt-breakdown-body">
      <StatRow label="Labor" value={minorToMoney(attempt.baseMinor, attempt.currency)} />
      <StatRow label="Fixed costs" value={minorToMoney(attempt.fixedCostsMinor || 0, attempt.currency)} />
      <StatRow label="Payer addition" value={minorToMoney(attempt.flatAdditionMinor || 0, attempt.currency)} />
      <StatRow label="Tax" value={minorToMoney(attempt.taxMinor, attempt.currency)} />
      <StatRow
        label="Estimated payer processor fee"
        value={minorToMoney(attempt.quotedPayerProcessorFeeMinor, attempt.currency)}
      />
      {#each items as item (item._id)}
        <ReceiptItemRow {item} currency={attempt.currency} compact />
      {/each}
    </div>
  </details>

  <!-- Provider sync is machine state, not contribution history, so it stops
       borrowing the blue the title block reserves for money that arrived. -->
  <p class="attempt-sync quiet">{syncParts.join(' · ')}</p>
</article>
{#each risks as risk (risk._id)}
  <RiskCard {risk} />
{/each}
